import type { GisDeltas, GisLens, MandalAggregate, SiteStatus } from '../api/types'

/** Fields a lens reads off a mandal row. */
type LensInput = Pick<MandalAggregate, 'coverage' | 'activeIssues' | 'officerAvgCompletion'>

export interface LensConfig {
  id: GisLens
  label: string
  /** Hex accent driving the active tab, hero gradient, ranking bars, lens chip. */
  accent: string
  /** Raw metric for the lens. `null` = no data for this mandal (render "—"/grey). */
  value: (m: LensInput) => number | null
  /** Format a (possibly null) value for display. */
  fmt: (v: number | null) => string
  /** Which `deltas` field this lens compares against. */
  delta: (d: GisDeltas) => number | null
  /** 5-step choropleth ramp, light → dark. */
  ramp: readonly [string, string, string, string, string]
  /** True when a HIGHER value is WORSE (issues): flips ranking order + delta colors. */
  invert: boolean
  /** Legend rows: [label, color]. */
  legend: readonly (readonly [string, string])[]
}

/** Fill for a mandal with no data for the active lens. */
export const LENS_NO_DATA = '#e5e7eb'

const pct = (v: number | null): string => (v === null ? '—' : `${v}%`)

export const LENSES: Record<GisLens, LensConfig> = {
  coverage: {
    id: 'coverage', label: 'Coverage', accent: '#2563eb',
    value: (m) => m.coverage, fmt: pct, delta: (d) => d.coverage,
    ramp: ['#dbeafe', '#93c5fd', '#3b82f6', '#1d4ed8', '#1e3a8a'], invert: false,
    legend: [['85%+', '#dbeafe'], ['70–85%', '#93c5fd'], ['55–70%', '#3b82f6'], ['40–55%', '#1d4ed8'], ['<40%', '#1e3a8a']],
  },
  issues: {
    id: 'issues', label: 'Issues', accent: '#dc2626',
    value: (m) => m.activeIssues, fmt: (v) => (v === null ? '—' : `${v}`), delta: (d) => d.activeIssues,
    ramp: ['#fee2e2', '#fca5a5', '#f87171', '#ef4444', '#b91c1c'], invert: true,
    legend: [['0-5', '#fee2e2'], ['6-15', '#f87171'], ['16-25', '#ef4444'], ['>25', '#b91c1c']],
  },
  officers: {
    id: 'officers', label: 'Officers', accent: '#7c3aed',
    value: (m) => m.officerAvgCompletion, fmt: pct, delta: (d) => d.officers,
    ramp: ['#ede9fe', '#c4b5fd', '#a78bfa', '#8b5cf6', '#6d28d9'], invert: false,
    legend: [['85%+', '#ede9fe'], ['70–85%', '#c4b5fd'], ['55–70%', '#a78bfa'], ['40–55%', '#8b5cf6'], ['<40%', '#6d28d9']],
  },
}

/** Issues band by active-issue count (higher = worse = darker), aligned to the issues legend. */
export const issuesBandIndex = (v: number): number => (v <= 5 ? 0 : v <= 15 ? 2 : v <= 25 ? 3 : 4)

/**
 * Choropleth fill for a FILTER-REACTIVE per-mandal issue count (the Issues-tab map). A count of 0
 * means "no issues match the active filters" → neutral grey (distinct from 1–5, the lightest red);
 * 1+ indexes the issues ramp by the same FIXED bands as the legend, so a colour is stable per render.
 */
export function issueCountColor(count: number): string {
  return count <= 0 ? LENS_NO_DATA : LENSES.issues.ramp[issuesBandIndex(count)]!
}
/** Coverage/Officers % band (V2 decision D4): 85+ / 70–85 / 55–70 / 40–55 / <40 → ramp 0..4. */
const pctBandIndex = (v: number): number => (v >= 85 ? 0 : v >= 70 ? 1 : v >= 55 ? 2 : v >= 40 ? 3 : 4)

/**
 * Fixed band index into a lens ramp, aligned to that lens's legend thresholds (V2 decision D4).
 * Stable across periods — a colour means the same thing every time, unlike a min-max normalize.
 */
function lensBandIndex(lens: GisLens, v: number): number {
  return lens === 'issues' ? issuesBandIndex(v) : pctBandIndex(v)
}

/**
 * Choropleth fill for one mandal: index the lens value into its 5-step ramp by FIXED band (not a
 * min-max normalize — see `lensBandIndex`). Used for the Coverage / Officers maps; the Overview /
 * Issues maps shade by the Attention Score (`attentionColor`) instead. Null → neutral grey.
 */
export function lensColor(lens: GisLens, m: MandalAggregate): string {
  const v = LENSES[lens].value(m)
  if (v === null) {
    return LENS_NO_DATA
  }
  return LENSES[lens].ramp[lensBandIndex(lens, v)]!
}

/** '#rrggbb' + alpha → 'rgba(r,g,b,a)'. */
export function hexA(hex: string, a: number): string {
  const n = parseInt(hex.slice(1), 16)
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`
}

export const SITE_STATUS_META: Record<SiteStatus, { label: string; color: string }> = {
  surveyed: { label: 'Surveyed', color: '#10b981' },
  in_progress: { label: 'In Progress', color: '#f59e0b' },
  overdue: { label: 'Overdue', color: '#ef4444' },
  never_surveyed: { label: 'Never Surveyed', color: '#94a3b8' },
}

/**
 * Status PILL tints (bg/text/border) + label, for the Coverage dock site rows + subbar note. Distinct
 * from `SITE_STATUS_META` (which owns the dot/arc/legend hex): these are the mock's pill triples, and
 * `never_surveyed` reads "Never visited" (the mock's STLABEL word) — NOT the legend's label.
 */
export const SITE_STATUS_PILL: Record<
  SiteStatus,
  { bg: string; text: string; border: string; label: string }
> = {
  surveyed: { bg: 'bg-[#f0fdf4]', text: 'text-[#15803d]', border: 'border-[#bbf7d0]', label: 'Surveyed' },
  in_progress: { bg: 'bg-[#fffbeb]', text: 'text-[#b45309]', border: 'border-[#fde68a]', label: 'In progress' },
  overdue: { bg: 'bg-[#fef2f2]', text: 'text-[#b91c1c]', border: 'border-[#fecaca]', label: 'Overdue' },
  never_surveyed: { bg: 'bg-[#f1f5f9]', text: 'text-[#475569]', border: 'border-[#e2e8f0]', label: 'Never visited' },
}
